/**
 * File I/O utilities for workspace import/export
 */

import logger from "../../utils/logger";
import { WORKSPACE_CONFIG } from "../constants.js";

/**
 * Downloads a file using the browser's download API with better browser compatibility
 * @param {string} content - File content as string
 * @param {string} filename - Name of the file to download
 * @param {string} mimeType - MIME type of the file
 */
export const downloadFile = (
  content,
  filename,
  mimeType = WORKSPACE_CONFIG.FILE_MIME_TYPE,
) => {
  return new Promise((resolve, reject) => {
    try {
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.style.display = "none";

      // Add event listeners to detect if download starts
      let downloadStarted = false;

      const handleDownloadStart = () => {
        downloadStarted = true;
        cleanup();
        resolve(true);
      };

      const handleDownloadError = () => {
        cleanup();
        reject(new Error("Download was blocked or failed"));
      };

      // Some browsers support these events
      link.addEventListener("click", handleDownloadStart);
      link.addEventListener("error", handleDownloadError);

      document.body.appendChild(link);

      // Try to trigger download
      link.click();

      // Fallback: if no event fired within a short time, assume it was blocked
      setTimeout(() => {
        if (!downloadStarted) {
          cleanup();
          reject(new Error("Download was blocked by browser security policy"));
        }
      }, 1000);

      const cleanup = () => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        link.removeEventListener("click", handleDownloadStart);
        link.removeEventListener("error", handleDownloadError);
      };
    } catch (error) {
      console.error("Failed to initiate download:", error);

      // Log de erro para o usuário
      logger.workspace.error("Falha ao baixar arquivo", {
        error: error.message,
        filename: filename,
      });

      reject(new Error("Unable to download file. Please try again."));
    }
  });
};

/**
 * Alternative download method using window.open (works better in some browsers)
 * @param {string} content - File content as string
 * @param {string} filename - Name of the file to download (not used in this method)
 */
export const downloadFileAlternative = (content, filename) => {
  // eslint-disable-line no-unused-vars
  return new Promise((resolve, reject) => {
    try {
      const blob = new Blob([content], {
        type: WORKSPACE_CONFIG.FILE_MIME_TYPE,
      });
      const url = URL.createObjectURL(blob);

      // Try using window.open as an alternative
      const newWindow = window.open(url, "_blank");

      if (newWindow) {
        // If window opened successfully, close it after a short delay
        setTimeout(() => {
          newWindow.close();
          URL.revokeObjectURL(url);
          resolve(true);
        }, 100);
      } else {
        // If popup was blocked, try the regular download method
        URL.revokeObjectURL(url);
        reject(new Error("Popup blocked, trying alternative method"));
      }
    } catch (error) {
      console.error("Alternative download method failed:", error);
      reject(new Error("Alternative download method failed"));
    }
  });
};

/**
 * Reads a file from the user's file system
 * @param {File} file - The file to read
 * @returns {Promise<string>} - File content as string
 */
export const readFile = (file) => {
  return new Promise((resolve, reject) => {
    console.log(
      "Reading file:",
      file.name,
      "Size:",
      file.size,
      "Type:",
      file.type,
    );

    if (!file) {
      reject(new Error("No file provided"));
      return;
    }

    if (file.size === 0) {
      reject(new Error("File is empty"));
      return;
    }

    const reader = new FileReader();

    reader.onload = (event) => {
      console.log(
        "File read successfully, content length:",
        event.target.result.length,
      );

      // Log de sucesso para o usuário
      logger.workspace.success("Arquivo lido com sucesso", {
        filename: file.name,
        contentLength: event.target.result.length,
      });

      resolve(event.target.result);
    };

    reader.onerror = (event) => {
      console.error("FileReader error:", event);

      // Log de erro para o usuário
      logger.workspace.error("Erro ao ler arquivo", {
        filename: file.name,
        error: event.target?.error?.message || "Erro desconhecido",
      });

      reject(new Error("Failed to read file"));
    };

    reader.onabort = () => {
      console.warn("File reading was aborted");
      reject(new Error("File reading was aborted"));
    };

    try {
      reader.readAsText(file);
    } catch (error) {
      console.error("Error starting file read:", error);
      reject(new Error("Failed to start reading file"));
    }
  });
};

/**
 * Opens a file picker dialog for loading workspace files
 * @returns {Promise<{file: File|null, cancelled: boolean, error: Error|null}>} - Result object
 */
export const openFilePicker = () => {
  return new Promise((resolve) => {
    console.log("Opening file picker...");

    const input = document.createElement("input");
    input.type = "file";
    input.accept = WORKSPACE_CONFIG.FILE_ACCEPT;
    input.style.display = "none";
    input.multiple = false; // Only allow single file selection

    let resolved = false;

    const cleanup = () => {
      if (!resolved && document.body.contains(input)) {
        document.body.removeChild(input);
      }
    };

    const resolveResult = (file, cancelled = false, error = null) => {
      resolved = true;
      console.log("File picker result:", {
        file: file ? file.name : null,
        cancelled,
        error: error?.message,
      });
      cleanup();
      resolve({ file, cancelled, error });
    };

    input.onchange = (event) => {
      const file = event.target.files[0];
      console.log("File selected:", file ? file.name : "none");
      if (file) {
        console.log("File details:", {
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified,
        });
      }
      resolveResult(file, false, null);
    };

    input.oncancel = () => {
      console.log("File picker cancelled by user");
      resolveResult(null, true, null);
    };

    // Add error handling for the input element
    input.onerror = (error) => {
      console.error("File input error:", error);
      resolveResult(null, false, error);
    };

    document.body.appendChild(input);

    try {
      // Use requestAnimationFrame to ensure the element is properly added to DOM
      requestAnimationFrame(() => {
        input.click();
      });
    } catch (error) {
      resolved = true;
      console.error("Error opening file picker:", error);
      cleanup();
      resolve({ file: null, cancelled: false, error });
    }

    // Set a timeout in case the dialog never opens or user doesn't respond
    setTimeout(() => {
      if (!resolved) {
        resolved = true;
        console.warn("File picker timeout - no response from user");
        cleanup();
        resolve({
          file: null,
          cancelled: false,
          error: new Error("File picker timeout"),
        });
      }
    }, WORKSPACE_CONFIG.FILE_PICKER_TIMEOUT);
  });
};
